import { Link } from 'react-router-dom';
import { Github, Twitter, Linkedin, Youtube, Mail } from 'lucide-react';
import { Logo } from '@/components/ui/Logo';
import { FlagIcon } from '@/components/ui/FlagIcon';
import { useTranslation } from 'react-i18next';

const footerLinksConfig = {
  product: {
    titleKey: 'footer.product',
    links: [
      { nameKey: 'footer.features', href: '/features' },
      { nameKey: 'footer.enterprise', href: '/enterprise' },
      { nameKey: 'footer.pricing', href: '/pricing' },
      { nameKey: 'footer.download', href: '/download' },
    ],
  },
  resources: {
    titleKey: 'footer.resources',
    links: [
      { nameKey: 'footer.documentation', href: '/docs' },
      { nameKey: 'footer.changelog', href: '/changelog' },
      { nameKey: 'footer.blog', href: '/blog' },
      { nameKey: 'footer.status', href: '/status' },
    ],
  },
  company: {
    titleKey: 'footer.company',
    links: [
      { nameKey: 'footer.about', href: '/about' },
      { nameKey: 'footer.careers', href: '/careers' },
      { nameKey: 'footer.community', href: '/community' },
      { nameKey: 'footer.contact', href: '/contact' },
    ],
  },
  legal: {
    titleKey: 'footer.legal',
    links: [
      { nameKey: 'footer.terms', href: '/terms' },
      { nameKey: 'footer.privacy', href: '/privacy' },
      { nameKey: 'footer.security', href: '/security' },
    ],
  },
};

const socialLinks = [
  { name: 'GitHub', href: 'https://github.com/ochtopus', icon: Github },
  { name: 'Twitter', href: 'https://twitter.com/ochtopus', icon: Twitter },
  { name: 'LinkedIn', href: 'https://linkedin.com/company/ochtopus', icon: Linkedin },
  { name: 'YouTube', href: 'https://youtube.com/@ochtopus', icon: Youtube },
];

export function Footer() {
  const { t } = useTranslation();

  return (
    <footer className="footer">
      <div className="container">
        <div className="footer-grid">
          {/* Brand Column */}
          <div className="col-span-2 lg:col-span-1">
            <Link to="/" className="inline-block mb-4">
              <Logo size={32} />
            </Link>
            <p className="text-text-secondary text-sm mb-4 max-w-xs">
              {t('footer.tagline')}
            </p>

            {/* Made in Turkey Badge */}
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-gradient-to-r from-red-500/10 to-white/10 border border-red-500/30 mb-4">
              <FlagIcon code="tr" size={20} />
              <span className="text-xs font-medium text-text-secondary">
                {t('footer.madeInTurkey')}
              </span>
            </div>
            <div className="flex items-center gap-3 mb-4">
              {socialLinks.map((item) => (
                <a
                  key={item.name}
                  href={item.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="p-2 rounded-lg text-text-secondary hover:text-accent hover:bg-accent-glow transition-colors"
                  aria-label={item.name}
                >
                  <item.icon size={18} />
                </a>
              ))}
            </div>
            <div className="space-y-2 text-sm">
              <a href="mailto:hello@ochtopus.com" className="flex items-center gap-2 text-text-secondary hover:text-accent transition-colors">
                <Mail size={14} />
                hello@ochtopus.com
              </a>
              <a href="mailto:support@ochtopus.com" className="flex items-center gap-2 text-text-secondary hover:text-accent transition-colors">
                <Mail size={14} />
                support@ochtopus.com
              </a>
            </div>
          </div>

          {/* Link Columns */}
          {Object.entries(footerLinksConfig).map(([key, section]) => (
            <div key={key}>
              <h3 className="footer-heading">{t(section.titleKey)}</h3>
              <ul className="space-y-1">
                {section.links.map((link) => (
                  <li key={link.nameKey}>
                    {link.href.startsWith('/') ? (
                      <Link to={link.href} className="footer-link">
                        {t(link.nameKey)}
                      </Link>
                    ) : (
                      <a href={link.href} className="footer-link">
                        {t(link.nameKey)}
                      </a>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* Bottom Bar */}
        <div className="mt-12 pt-6 border-t border-border-default flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="text-text-tertiary text-sm">
            &copy; {new Date().getFullYear()} Ochtopus. {t('footer.copyright')}
          </p>
          <div className="flex items-center gap-4">
            <Link
              to="/status"
              className="flex items-center gap-2 text-text-tertiary text-sm hover:text-accent transition-colors"
            >
              <span className="w-2 h-2 rounded-full bg-success animate-pulse"></span>
              {t('footer.allSystemsOperational')}
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
